import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Eye, EyeOff, Loader2, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/infrastructure/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/shared/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { requestMagicFill } from "@/shared/components/form/magicFillRequest";
import { useToast } from "@/shared/hooks/use-toast";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import type { ApiError } from "@/shared/utils/fetchApi";

import { JobsService } from "../services/jobsService";
import type { Job, JobFieldError, JobInput, JobWebhookAuthType, JobWebhookHandoff } from "../types/job";
import { CopyField } from "./CopyField";
import { FrequencyPicker } from "./FrequencyPicker";
import { JobTestDialog } from "./JobTestDialog";
import { MentionTextarea } from "./MentionTextarea";
import { draftCron, draftFromCron, draftProblem, type FrequencyDraft } from "../utils/frequencyDraft";
import { applyProposal, proposalRequest } from "../utils/jobProposal";
import { DEFAULT_TIMEZONE, defaultTimezone } from "../utils/schedule";

interface JobFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string;
  /** Ausente na criação. */
  job?: Job | null;
  /** `handoff` vem quando há webhook a repassar: criado agora, ou com credencial nova. */
  onSaved: (result: { id: string; handoff: JobWebhookHandoff | null }) => void;
  /**
   * Criação a partir de uma skill (task 86874): o nome dela. O formulário abre
   * com a skill citada e pede ao Magic Fill uma proposta de job.
   */
  seedSkill?: string | null;
}

const DEFAULT_CRON = "0 9 * * 1-5";

type ProposalStage = "loading" | "filling" | "finishing";
const PROPOSAL_STAGES: ProposalStage[] = ["loading", "filling", "finishing"];
const FINISHING_PAUSE_MS = 600;

/**
 * As etapas da proposta a partir de uma skill (task 86874), sobre o formulário
 * esmaecido: o que já foi feito marcado, o que está em curso girando. Sem isto
 * o formulário abria vazio e se preenchia de uma vez, sem sinal de que algo
 * acontecia.
 */
function ProposalProgress({ stage }: { stage: ProposalStage }) {
  const { t } = useTranslation();
  const current = PROPOSAL_STAGES.indexOf(stage);
  return (
    <div className="absolute inset-x-0 top-6 z-10 flex justify-center">
      <div
        role="status"
        aria-live="polite"
        className="w-full max-w-sm rounded-lg border border-border bg-popover p-4 shadow-lg"
      >
        <p className="mb-3 flex items-center gap-2 text-sm font-medium">
          <Sparkles className="h-4 w-4 text-primary" /> {t("jobs.proposal.title")}
        </p>
        <ol className="space-y-2">
          {PROPOSAL_STAGES.map((step, index) => (
            <li
              key={step}
              className={cn(
                "flex items-center gap-2 text-sm",
                index > current ? "text-muted-foreground/60" : "text-foreground",
              )}
            >
              {index < current ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600 dark:text-green-500" />
              ) : index === current ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
              ) : (
                <span className="h-4 w-4 shrink-0 rounded-full border border-muted-foreground/40" />
              )}
              {t(`jobs.proposal.stages.${step}`)}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

/**
 * Criar e editar um Job pela tela (AB#86605, AC13): instrução e gatilho, sem
 * abrir o editor de fluxo. Não há campo de Skill: a instrução a cita pelo nome,
 * como numa conversa, e o agente a carrega. O horário sai do seletor guiado
 * (`FrequencyPicker`, AB#87330), com o cron como opção avançada.
 *
 * A api é a autoridade da validação: os erros dela voltam por campo e aparecem
 * embaixo de cada um. Aqui só se evita mandar o que certamente está incompleto.
 */
export function JobFormDialog({ open, onOpenChange, productId, job, onSaved, seedSkill }: JobFormDialogProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const editing = Boolean(job);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [instruction, setInstruction] = useState("");
  const [triggerType, setTriggerType] = useState<"schedule" | "webhook">("schedule");
  const [frequency, setFrequency] = useState<FrequencyDraft>(() => draftFromCron(DEFAULT_CRON));
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [authType, setAuthType] = useState<JobWebhookAuthType>("basic");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [timeout, setTimeoutMinutes] = useState("");
  const [maxWrites, setMaxWrites] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  // Publicar na tela principal (task 86877): Testar grava o rascunho sem
  // publicar; Publicar grava e publica. Um job criado pelo Testar fica aqui,
  // para o Publicar seguinte atualizá-lo em vez de criar outro.
  const [busy, setBusy] = useState<"testing" | "publishing" | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [testJob, setTestJob] = useState<Job | null>(null);
  // O token de um webhook criado pelo Testar só volta naquela gravação: fica
  // guardado para a entrega no fim, depois de publicar.
  const pendingHandoff = useRef<JobWebhookHandoff | null>(null);
  // A etapa da proposta a partir de uma skill, à vista enquanto ela é montada.
  const [proposalStage, setProposalStage] = useState<ProposalStage | null>(null);
  const proposing = proposalStage !== null;
  const [proposalReason, setProposalReason] = useState("");
  // Uma proposta por abertura: reabrir o formulário pede de novo, mudar de
  // aba ou re-renderizar não.
  const proposalRequested = useRef(false);
  const proposalAbort = useRef<AbortController | null>(null);

  // Abrir o diálogo reinicia o formulário com o job em edição, ou vazio.
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSavedId(null);
    setTestJob(null);
    setConfirmOpen(false);
    pendingHandoff.current = null;
    setName(job?.name ?? "");
    setDescription(job?.description ?? "");
    setInstruction(job?.instruction ?? "");
    setTimeoutMinutes(job?.timeout_minutes ? String(job.timeout_minutes) : "");
    setMaxWrites(job?.max_writes !== null && job?.max_writes !== undefined ? String(job.max_writes) : "");
    setAuthType(job?.trigger.type === "webhook" ? job.trigger.auth_type : "basic");
    setUsername("");
    setPassword("");
    setShowPassword(false);
    if (job?.trigger.type === "schedule") {
      setTriggerType("schedule");
      setTimezone(job.trigger.timezone || DEFAULT_TIMEZONE);
      setFrequency(draftFromCron(job.trigger.cron));
    } else {
      setTriggerType(job ? "webhook" : "schedule");
      setFrequency(draftFromCron(DEFAULT_CRON));
      // Job novo: o fuso de quem cria (não há fuso no usuário nem no workspace).
      setTimezone(defaultTimezone());
    }
  }, [open, job]);

  // Na edição de um webhook, endereço e credencial vêm do detalhe do job — a
  // lista não os traz. Quem administra recebe a senha para conferir ou trocar.
  const editingWebhook = open && job?.trigger.type === "webhook";
  // O que a instrução pode citar com `@` (task 86871).
  const referencesQuery = useQuery({
    queryKey: ["job-references", productId],
    queryFn: () => JobsService.references(productId),
    enabled: open && Boolean(productId),
    staleTime: 60_000,
  });

  const detailQuery = useQuery({
    queryKey: ["job-detail", productId, job?.id],
    queryFn: () => JobsService.get(productId, job!.id),
    enabled: editingWebhook,
    staleTime: 0,
  });
  const loaded = editingWebhook ? (detailQuery.data?.webhook ?? null) : null;

  useEffect(() => {
    if (!loaded) return;
    setAuthType(loaded.auth_type);
    setUsername(loaded.username ?? "");
    setPassword(loaded.password ?? "");
  }, [loaded]);

  // A proposta a partir de uma skill (task 86874). A citação entra na hora, e o
  // resto chega do Magic Fill: nome, descrição, instrução e uma frequência,
  // com o motivo dela. Nada é salvo — quem administra revisa e salva.
  useEffect(() => {
    if (!open) {
      // Só fechar o formulário cancela o pedido: uma dependência que muda no
      // meio (as referências recarregadas, por exemplo) não pode derrubá-lo,
      // porque ele é feito uma vez só por abertura.
      proposalAbort.current?.abort();
      proposalAbort.current = null;
      proposalRequested.current = false;
      setProposalReason("");
      setProposalStage(null);
      return;
    }
    if (job || !seedSkill || proposalRequested.current) return;
    // As skills e os campos do workspace ainda chegando: o formulário já abre
    // em carregamento, em vez de vazio. Se a leitura falhar, segue sem a
    // descrição da skill — o nome basta para a IA.
    if (!referencesQuery.data && !referencesQuery.isError) {
      setProposalStage("loading");
      return;
    }
    proposalRequested.current = true;

    const skill = referencesQuery.data?.skills.find((s) => s.name === seedSkill) ?? {
      name: seedSkill,
      title: null,
      description: "",
    };
    setInstruction(`@skill:${seedSkill} `);
    setProposalStage("filling");
    const controller = new AbortController();
    proposalAbort.current = controller;
    const { fields, fieldMetadata } = proposalRequest(skill);
    requestMagicFill({
      formType: "job_from_skill",
      fields,
      metadata: { productId, fieldMetadata },
      signal: controller.signal,
      language: i18n.language,
    })
      .then(async (result) => {
        // Os últimos ajustes são reais: a resposta é conferida (a skill citada,
        // um cron válido) antes de entrar no formulário. A pausa curta deixa a
        // etapa legível em vez de piscar.
        setProposalStage("finishing");
        const proposal = applyProposal(result.suggestedFields, seedSkill);
        await new Promise((resolve) => setTimeout(resolve, FINISHING_PAUSE_MS));
        if (controller.signal.aborted) return;
        if (proposal.name) setName(proposal.name);
        if (proposal.description) setDescription(proposal.description);
        setInstruction(proposal.instruction);
        setTriggerType(proposal.triggerType);
        if (proposal.triggerType === "schedule") setFrequency(draftFromCron(proposal.cron));
        setProposalReason(proposal.reason || t("jobs.proposal.ready"));
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        // Sem proposta o formulário segue útil: a skill já está citada, e o
        // resto se preenche à mão.
        toast({ title: t("jobs.proposal.failed"), description: resolveApiErrorMessage(error, t) });
      })
      .finally(() => {
        if (!controller.signal.aborted) setProposalStage(null);
      });
  }, [open, job, seedSkill, referencesQuery.data, referencesQuery.isError, productId, i18n.language, t, toast]);

  const localErrors = (): Record<string, string> => {
    const found: Record<string, string> = {};
    if (!name.trim()) found.name = t("jobs.form.required");
    if (!instruction.trim()) found.instruction = t("jobs.form.required");
    if (triggerType === "webhook" && authType === "basic") {
      if (!username.trim()) found["trigger.auth.username"] = t("jobs.form.required");
      // Senha nova só é obrigatória quando não há uma guardada para o basic.
      if (!password && loaded?.auth_type !== "basic") found["trigger.auth.password"] = t("jobs.form.required");
    }
    // O seletor já mostra o problema no próprio campo; aqui ele só impede salvar.
    if (triggerType === "schedule" && draftProblem(frequency)) found.schedule = t("jobs.form.invalidCron");
    return found;
  };

  const currentId = job?.id ?? savedId;
  // Ainda não roda: novo, ou rascunho (criado pela IA, ou testado aqui antes de publicar).
  const firstPublish = !job?.published;

  /** Grava o formulário — cria ou atualiza o rascunho — e devolve o id, ou null se não deu. */
  const save = async (): Promise<string | null> => {
    const found = localErrors();
    setErrors(found);
    if (Object.keys(found).length > 0) return null;

    // A credencial só vai quando muda: reenviar a mesma regravaria o segredo
    // sem motivo. Num webhook novo (ou que acabou de virar webhook) ela sempre
    // vai — sem senha no bearer, o Prodgy gera o token.
    const webhookAuth = {
      type: authType,
      ...(authType === "basic" ? { username: username.trim() } : {}),
      ...(password ? { password } : {}),
    };
    const credentialChanged =
      triggerType === "webhook" &&
      (!loaded ||
        loaded.auth_type !== authType ||
        (authType === "basic" && (loaded.username ?? "") !== username.trim()) ||
        (loaded.password ?? "") !== password);

    const input: JobInput = {
      name: name.trim(),
      description: description.trim(),
      instruction: instruction.trim(),
      trigger:
        triggerType === "schedule"
          ? { type: "schedule", cron: draftCron(frequency), timezone: timezone.trim() || DEFAULT_TIMEZONE }
          : { type: "webhook", ...(credentialChanged ? { auth: webhookAuth } : {}) },
    };
    if (timeout.trim()) input.timeout_minutes = Number(timeout);
    if (maxWrites.trim()) input.max_writes = Number(maxWrites);

    try {
      const result = currentId
        ? await JobsService.update(productId, currentId, input)
        : await JobsService.create(productId, input);
      const generated = result.generated_secrets?.[0]?.token;
      if (triggerType === "webhook" && result.webhook_url) {
        pendingHandoff.current = {
          url: result.webhook_url,
          auth_type: authType,
          ...(authType === "basic" ? { username: username.trim() } : {}),
          ...(generated || password
            ? { secret: generated ?? password }
            : pendingHandoff.current?.secret
              ? { secret: pendingHandoff.current.secret }
              : {}),
        };
      }
      setSavedId(result.id);
      return result.id;
    } catch (error) {
      const fieldErrors = ((error as ApiError)?.data as { errors?: JobFieldError[] } | undefined)?.errors;
      if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
        // Erro de campo fica no campo; o que não tem campo vai para o toast.
        const byField: Record<string, string> = {};
        for (const e of fieldErrors) byField[e.field ?? "_"] = e.message;
        setErrors(byField);
        if (byField._) toast({ title: byField._, variant: "destructive" });
      } else {
        toast({ title: resolveApiErrorMessage(error, t), variant: "destructive" });
      }
      return null;
    }
  };

  /** Testar: grava o rascunho sem publicar e roda o teste por cima do formulário. */
  const handleTest = async () => {
    setBusy("testing");
    setSaving(true);
    try {
      const id = await save();
      if (!id) return;
      setTestJob({
        ...(job ?? {}),
        id,
        name: name.trim(),
        published: job?.published ?? false,
      } as Job);
    } finally {
      setBusy(null);
      setSaving(false);
    }
  };

  /** Publicar: grava e publica. O job passa a disparar com o que está no formulário. */
  const handlePublish = async () => {
    setConfirmOpen(false);
    setBusy("publishing");
    setSaving(true);
    try {
      const id = await save();
      if (!id) return;
      await JobsService.publish(productId, id);
      toast({ title: t("jobs.publish.success") });
      onSaved({ id, handoff: pendingHandoff.current });
    } catch (error) {
      // Gravou e não publicou: o rascunho fica, e a mensagem diz o motivo.
      toast({ title: resolveApiErrorMessage(error, t), variant: "destructive" });
    } finally {
      setBusy(null);
      setSaving(false);
    }
  };

  const formValid = Object.keys(localErrors()).length === 0;

  const fieldError = (field: string) =>
    errors[field] ? <p className="text-xs text-destructive">{errors[field]}</p> : null;

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t(editing ? "jobs.form.editTitle" : "jobs.form.createTitle")}</DialogTitle>
          <DialogDescription>{t("jobs.form.description")}</DialogDescription>
        </DialogHeader>

        <div className="relative">
          {proposalStage && <ProposalProgress stage={proposalStage} />}
        <div
          className={cn("space-y-5 transition-opacity", proposing && "pointer-events-none select-none opacity-30")}
          aria-busy={proposing}
        >
          {!proposing && proposalReason && (
            <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <p className="text-muted-foreground">{t("jobs.proposal.reason", { reason: proposalReason })}</p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="job-name">{t("jobs.form.name")}</Label>
            <Input
              id="job-name"
              value={name}
              placeholder={t("jobs.form.namePlaceholder")}
              onChange={(e) => setName(e.target.value)}
            />
            {fieldError("name")}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="job-description">{t("jobs.form.descriptionLabel")}</Label>
            <Input
              id="job-description"
              value={description}
              placeholder={t("jobs.form.descriptionPlaceholder")}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="job-instruction">{t("jobs.form.instruction")}</Label>
            <MentionTextarea
              id="job-instruction"
              rows={4}
              value={instruction}
              placeholder={t("jobs.form.instructionPlaceholder")}
              onChange={setInstruction}
              references={referencesQuery.data}
            />
            <p className="text-xs text-muted-foreground">{t("jobs.form.instructionHelp")}</p>
            {fieldError("instruction")}
          </div>

          <div className="space-y-2">
            <Label>{t("jobs.form.trigger")}</Label>
            <RadioGroup
              value={triggerType}
              onValueChange={(value) => setTriggerType(value as "schedule" | "webhook")}
              className="flex flex-wrap gap-4"
            >
              <label className="flex items-center gap-2 text-sm">
                <RadioGroupItem value="schedule" /> {t("jobs.form.triggerSchedule")}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <RadioGroupItem value="webhook" /> {t("jobs.form.triggerWebhook")}
              </label>
            </RadioGroup>

            {triggerType === "webhook" ? (
              <div className="space-y-3 rounded-md border border-border/60 p-3">
                <p className="text-xs text-muted-foreground">{t("jobs.form.webhookHelp")}</p>
                {editingWebhook && detailQuery.isLoading ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("jobs.webhook.loading")}
                  </div>
                ) : (
                  <>
                    {loaded?.url && <CopyField label={t("jobs.webhook.url")} value={loaded.url} />}
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label>{t("jobs.webhook.authType")}</Label>
                        <Select value={authType} onValueChange={(value) => setAuthType(value as JobWebhookAuthType)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="basic">{t("jobs.webhook.basic")}</SelectItem>
                            <SelectItem value="bearer">{t("jobs.webhook.bearer")}</SelectItem>
                          </SelectContent>
                        </Select>
                        {fieldError("trigger.auth.type")}
                      </div>
                      {authType === "basic" && (
                        <div className="space-y-1.5">
                          <Label htmlFor="job-webhook-username">{t("jobs.webhook.username")}</Label>
                          <Input
                            id="job-webhook-username"
                            autoComplete="off"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                          />
                          {fieldError("trigger.auth.username")}
                        </div>
                      )}
                      <div className={authType === "basic" ? "space-y-1.5" : "space-y-1.5 sm:col-span-2"}>
                        <Label htmlFor="job-webhook-password">
                          {t(authType === "basic" ? "jobs.webhook.password" : "jobs.webhook.token")}
                        </Label>
                        <div className="relative">
                          <Input
                            id="job-webhook-password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            className="pr-9"
                            value={password}
                            placeholder={authType === "bearer" && !loaded ? t("jobs.webhook.tokenPlaceholder") : undefined}
                            onChange={(e) => setPassword(e.target.value)}
                          />
                          <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:text-foreground"
                            aria-label={t(showPassword ? "jobs.webhook.hide" : "jobs.webhook.show")}
                            onClick={() => setShowPassword((v) => !v)}
                          >
                            {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                        {fieldError("trigger.auth.password")}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-1.5">
                <FrequencyPicker
                  draft={frequency}
                  onDraftChange={setFrequency}
                  timezone={timezone}
                  onTimezoneChange={setTimezone}
                  timezoneError={errors["trigger.timezone"]}
                />
                {fieldError("trigger.cron")}
              </div>
            )}
            {fieldError("trigger")}
            {fieldError("trigger.type")}
          </div>

          <details className="rounded-md border border-border/60 p-3">
            <summary className="cursor-pointer text-sm font-medium">{t("jobs.form.advanced")}</summary>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="job-timeout">{t("jobs.form.timeout")}</Label>
                <Input
                  id="job-timeout"
                  type="number"
                  min={1}
                  max={60}
                  value={timeout}
                  onChange={(e) => setTimeoutMinutes(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">{t("jobs.form.timeoutHelp")}</p>
                {fieldError("timeout_minutes")}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="job-max-writes">{t("jobs.form.maxWrites")}</Label>
                <Input
                  id="job-max-writes"
                  type="number"
                  min={0}
                  max={200}
                  value={maxWrites}
                  onChange={(e) => setMaxWrites(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">{t("jobs.form.maxWritesHelp")}</p>
                {fieldError("max_writes")}
              </div>
            </div>
          </details>
        </div>

        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t("jobs.form.cancel")}
          </Button>
          {/* Testar não publica: a versão que roda continua a mesma até Publicar. */}
          <Button variant="outline" onClick={handleTest} disabled={saving || proposing || !formValid}>
            {busy === "testing" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("jobs.form.test")}
          </Button>
          <Button onClick={() => (firstPublish ? setConfirmOpen(true) : handlePublish())} disabled={saving || proposing}>
            {busy === "publishing" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {busy === "publishing" ? t("jobs.form.publishing") : t("jobs.form.publish")}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Na primeira publicação o job começa a disparar, com efeitos reais: confirma antes. */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("jobs.publish.confirmTitle", { name: name.trim() })}</AlertDialogTitle>
            <AlertDialogDescription>{t("jobs.publish.confirmDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("jobs.publish.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handlePublish()}>{t("jobs.publish.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <JobTestDialog
        job={testJob}
        productId={productId}
        canPublish={false}
        onClose={() => setTestJob(null)}
        onPublish={() => undefined}
      />
    </Dialog>
  );
}
